/**
 * RequestRegistry - Centralized tracking and cancellation of HTTP requests
 *
 * This class manages AbortControllers for all HTTP requests, enabling:
 * - Automatic cancellation on page unload/refresh
 * - Automatic cancellation on route navigation
 * - Manual cancellation of specific requests
 * - Protection of critical requests (login, save, delete) from auto-cancel
 *
 * @example
 * // In httpService - auto-registration
 * const { controller, requestId } = registry.register(config);
 * config.signal = controller.signal;
 *
 * // Cancel what the navigation leaves behind
 * registry.cancelOnNavigation({ facilityChanged });
 */

/**
 * How long a request may live. Pass it as the axios config key `requestScope`.
 * - PAGE (default): belongs to the page that sent it; cancelled on any navigation, so a response
 *   from a page the user left cannot overwrite the next page's state.
 * - FACILITY: loads facility-wide data into the store (tags, scans, levels, point clouds...);
 *   cancelled only when the user leaves the facility. Moving between pages of the same facility
 *   (e.g. Analysis -> viewer) would otherwise cancel it and nothing loads it again.
 */
export const REQUEST_SCOPE = Object.freeze({ PAGE: 'page', FACILITY: 'facility' });

/** Axios config fragment for a facility-wide load: `HttpService.get({ url, ...FACILITY_REQUEST })`. */
export const FACILITY_REQUEST = Object.freeze({ requestScope: REQUEST_SCOPE.FACILITY });

class RequestRegistry {
  constructor() {
    /** @type {Map<string, { controller: AbortController, critical: boolean, scope: string, url: string }>} */
    this.pending = new Map();

    /** @type {number} Counter for generating unique request IDs */
    this.idCounter = 0;

    /** @type {boolean} Whether the registry has been initialized */
    this.initialized = false;
  }

  /**
   * Initialize the registry with page unload handler
   * Called once when httpService is instantiated
   */
  init() {
    if (this.initialized) return;

    this.setupPageUnloadHandler();
    this.initialized = true;
  }

  /**
   * Generate a unique request ID
   * @param {Object} config - Axios request config
   * @returns {string} Unique request identifier
   */
  generateId(config) {
    this.idCounter += 1;
    const method = config.method || 'GET';
    const url = config.url || 'unknown';
    return `${method}:${url}:${this.idCounter}:${Date.now()}`;
  }

  /**
   * Register a new request with the registry
   * @param {Object} config - Axios request config
   * @param {boolean} config.critical - If true, request won't be auto-cancelled
   * @param {string} [config.requestScope] - REQUEST_SCOPE value; anything else is PAGE
   * @returns {{ controller: AbortController, requestId: string }}
   */
  register(config) {
    const controller = new AbortController();
    const requestId = this.generateId(config);
    const critical = config.critical === true;
    const scope = config.requestScope === REQUEST_SCOPE.FACILITY ? REQUEST_SCOPE.FACILITY : REQUEST_SCOPE.PAGE;
    const url = config.url || 'unknown';

    this.pending.set(requestId, {
      controller,
      critical,
      scope,
      url
    });

    return { controller, requestId };
  }

  /**
   * Unregister a completed request
   * @param {string} requestId - The request ID to remove
   */
  unregister(requestId) {
    this.pending.delete(requestId);
  }

  /**
   * Cancel a specific request by ID
   * @param {string} requestId - The request ID to cancel
   */
  cancel(requestId) {
    const entry = this.pending.get(requestId);
    if (entry) {
      entry.controller.abort();
      this.pending.delete(requestId);
    }
  }

  /**
   * Cancel what a route navigation leaves behind: every non-critical PAGE request, plus the
   * FACILITY requests when the navigation leaves their facility. A navigation that stays on the
   * page (`pageChanged: false`, a query-only change) cancels nothing.
   * @param {{ facilityChanged: boolean, pageChanged?: boolean }} navigation
   * @returns {number} Number of requests cancelled
   */
  cancelOnNavigation({ facilityChanged, pageChanged = true }) {
    let cancelled = 0;
    if (!pageChanged && !facilityChanged) return cancelled;

    this.pending.forEach((entry, requestId) => {
      if (entry.critical) return;
      if (entry.scope === REQUEST_SCOPE.FACILITY && !facilityChanged) return;
      entry.controller.abort();
      this.pending.delete(requestId);
      cancelled++;
    });

    if (cancelled > 0) {
      console.debug(`[RequestRegistry] Cancelled ${cancelled} pending request(s) on navigation`);
    }

    return cancelled;
  }

  /**
   * Cancel all cancellable (non-critical) pending requests, whatever their scope
   * @returns {number} Number of requests cancelled
   */
  cancelAllCancellable() {
    let cancelled = 0;

    this.pending.forEach((entry, requestId) => {
      if (!entry.critical) {
        entry.controller.abort();
        this.pending.delete(requestId);
        cancelled++;
      }
    });

    if (cancelled > 0) {
      console.debug(`[RequestRegistry] Cancelled ${cancelled} pending request(s) on navigation`);
    }

    return cancelled;
  }

  /**
   * Cancel ALL pending requests (including critical ones)
   * Used on page unload/refresh
   * @returns {number} Number of requests cancelled
   */
  cancelAll() {
    const count = this.pending.size;

    this.pending.forEach((entry) => {
      entry.controller.abort();
    });
    this.pending.clear();

    if (count > 0) {
      console.debug(`[RequestRegistry] Cancelled all ${count} pending request(s) on unload`);
    }

    return count;
  }

  /**
   * Get count of pending requests
   * @returns {{ total: number, critical: number, cancellable: number, facility: number }}
   */
  getStats() {
    let critical = 0;
    let cancellable = 0;
    let facility = 0;

    this.pending.forEach((entry) => {
      if (entry.critical) {
        critical++;
      } else {
        cancellable++;
      }
      if (entry.scope === REQUEST_SCOPE.FACILITY) facility++;
    });

    return {
      total: this.pending.size,
      critical,
      cancellable,
      facility
    };
  }

  /**
   * Check if a request is still pending
   * @param {string} requestId - The request ID to check
   * @returns {boolean}
   */
  isPending(requestId) {
    return this.pending.has(requestId);
  }

  /**
   * Set up page unload handler to cancel all requests
   * Prevents requests from completing after page refresh/close
   */
  setupPageUnloadHandler() {
    // 'beforeunload' fires on refresh, tab close, or navigation away
    window.addEventListener('beforeunload', () => {
      this.cancelAll();
    });

    // 'pagehide' is more reliable on mobile and when navigating away
    window.addEventListener('pagehide', () => {
      this.cancelAll();
    });
  }
}

// Export singleton instance
export const requestRegistry = new RequestRegistry();

// Export class for testing purposes
export { RequestRegistry };
